import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { AlertCircle, Calendar, Clock, CheckCircle, Star, Eye, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import { ValidatedRatingSlider } from "./ui/validated-input";
import { classifyDecisionReviews, getReviewCounts } from "@/lib/review-classification";
import { clickableCardProps } from "@/lib/clickable-card";

interface RatingEntry {
  id: string;
  day: number;
  rating: number;
  date: string;
  notes?: string;
  outcome?: string;
  lessons?: string;
}

interface Decision {
  id: string;
  title: string;
  description?: string;
  rationale: string;
  context?: string;
  decision_date: string;
  day0_rating: number;
  review_intervals: number[];
  created_at: string;
  updated_at?: string;
  rating_history: RatingEntry[];
  tags: Array<{ id: string; name: string }>;
  emotions: Array<{ id: string; name: string }>;
  scheduled_reviews: Array<{
    id: string;
    day: number;
    due_date: string;
    status: string;
    completed_at?: string;
  }>;
}

interface ReviewsProps {
  decisions: Decision[];
  onViewDecision: (id: string) => void;
  onUpdateDecision: (decision: Decision) => void;
  onAddReviewRating: (decisionId: string, reviewDay: number, rating: number, notes: string, outcome: string, lessons: string) => Promise<void>;
  getCurrentRating: (decision: Decision) => number;
}

export function Reviews({
  decisions,
  onViewDecision,
  // onUpdateDecision, // Commented out unused prop
  onAddReviewRating,
  getCurrentRating
}: ReviewsProps) {
  const [selectedDecision, setSelectedDecision] = useState<Decision | null>(null);
  const [selectedReviewDay, setSelectedReviewDay] = useState<number>(0);
  const [reviewNotes, setReviewNotes] = useState("");
  const [currentRating, setCurrentRating] = useState([7]);
  const [outcome, setOutcome] = useState("");
  const [lessons, setLessons] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  // Classify decisions with reviews
  const dueNowDecisions = decisions.filter(d => {
    const { dueNowReviews } = classifyDecisionReviews(d);
    return dueNowReviews.length > 0;
  }).map(decision => ({
    ...decision,
    dueNowReviews: classifyDecisionReviews(decision).dueNowReviews
  }));

  const overdueDecisions = decisions.filter(d => {
    const { overdueReviews } = classifyDecisionReviews(d);
    return overdueReviews.length > 0;
  }).map(decision => ({
    ...decision,
    overdueReviews: classifyDecisionReviews(decision).overdueReviews
  }));

  const upcomingDecisions = decisions.filter(d => {
    const { upcomingReviews } = classifyDecisionReviews(d);
    return upcomingReviews.length > 0;
  }).map(decision => ({
    ...decision,
    upcomingReviews: classifyDecisionReviews(decision).upcomingReviews
  }));

  // Get review counts
  const { dueNowCount, overdueCount, upcomingCount } = getReviewCounts(decisions);

  const completeReview = async () => {
    if (!selectedDecision) return;

    setIsSubmittingReview(true);
    try {
      await onAddReviewRating(
        selectedDecision.id,
        selectedReviewDay,
        currentRating[0],
        reviewNotes,
        outcome,
        lessons
      );
      toast.success("Review completed successfully!");
      setSelectedDecision(null);
      setSelectedReviewDay(0);
      setReviewNotes("");
      setOutcome("");
      setLessons("");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const openReviewDialog = (decision: Decision, reviewDay: number) => {
    setSelectedDecision(decision);
    setSelectedReviewDay(reviewDay);
    setCurrentRating([getCurrentRating(decision)]);
  };

  const getRatingTrend = (decision: Decision) => {
    const currentRating = getCurrentRating(decision);
    const initialRating = decision.day0_rating;
    const diff = currentRating - initialRating;
    
    if (diff > 0.1) return { icon: TrendingUp, color: "text-green-600", value: `+${diff.toFixed(1)}` };
    if (diff < -0.1) return { icon: TrendingDown, color: "text-red-600", value: diff.toFixed(1) };
    return { icon: Minus, color: "text-gray-500", value: "0" };
  };


  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1>Reviews</h1>
          <p className="text-muted-foreground">
            Complete scheduled reviews and track decision outcomes
          </p>
        </div>

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Due Now</CardTitle>
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {dueNowCount}
              </div>
              <p className="text-xs text-muted-foreground">
                Reviews due today or soon
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overdue</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {overdueCount}
              </div>
              <p className="text-xs text-muted-foreground">
                Past due date
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
              <Clock className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {upcomingCount}
              </div>
              <p className="text-xs text-muted-foreground">
                Next 7 days
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="due-now" className="space-y-6">
          <TabsList>
            <TabsTrigger value="due-now">Due Now ({dueNowCount})</TabsTrigger>
            <TabsTrigger value="overdue">Overdue ({overdueCount})</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming ({upcomingCount})</TabsTrigger>
          </TabsList>

          <TabsContent value="due-now">
            <div className="space-y-4">
              {dueNowDecisions.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                    <p className="text-muted-foreground">No reviews due now!</p>
                    <p className="text-sm text-muted-foreground">You're all caught up.</p>
                  </CardContent>
                </Card>
              ) : (
                dueNowDecisions.map((decision) => {
                  const decisionCurrentRating = getCurrentRating(decision);
                  const trend = getRatingTrend(decision);
                  const TrendIcon = trend.icon;
                  
                  return (
                    <Card
                      key={decision.id}
                      className="group cursor-pointer hover:shadow-md transition-shadow"
                      {...clickableCardProps(() => onViewDecision(decision.id))}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg group-hover:text-primary">{decision.title}</CardTitle>
                            <CardDescription className="mt-1">
                              {decision.description}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-3 ml-4">
                            <div className="text-right">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm font-medium">{decisionCurrentRating}/10</span>
                                {decision.rating_history.length > 1 && (
                                  <>
                                    <TrendIcon className={`h-3 w-3 ml-1 ${trend.color}`} />
                                    <span className={`text-xs ${trend.color}`}>{trend.value}</span>
                                  </>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Initial: {decision.day0_rating}/10
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(decision.decision_date).toLocaleDateString()}
                          </div>
                          <div className="flex gap-1">
                            {decision.tags.map((tag) => (
                              <Badge key={tag.id} variant="secondary" className="text-xs">
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <div className="space-y-3">
                          <div>
                            <span className="text-sm font-medium">Due Now Reviews:</span>
                            <div className="flex gap-2 mt-1">
                              {decision.dueNowReviews.map((review) => (
                                <Badge 
                                  key={review.day} 
                                  variant="destructive"
                                  className="bg-orange-600"
                                >
                                  Day {review.day} - Due {review.due_date}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between pt-2 border-t">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => onViewDecision(decision.id)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Decision
                            </Button>
                            
                            <div className="flex gap-2">
                              {decision.dueNowReviews.map((review) => (
                                <Dialog key={review.day}>
                                  <DialogTrigger asChild>
                                    <Button 
                                      size="sm"
                                      onClick={() => openReviewDialog(decision, review.day)}
                                    >
                                      Complete Day {review.day} Review
                                    </Button>
                                  </DialogTrigger>
                                   <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                     <DialogHeader className="sticky top-0 bg-background z-10 pb-4">
                                       <DialogTitle>Day {review.day} Review</DialogTitle>
                                       <DialogDescription>
                                         Review your decision: "{decision.title}"
                                       </DialogDescription>
                                     </DialogHeader>
                                     
                                     <div className="space-y-6 py-4 px-1">
                                       {/* Original Decision Summary */}
                                       <div className="p-4 bg-muted rounded-lg">
                                         <h4 className="font-medium mb-2">Original Decision (Day 0)</h4>
                                         <p className="text-sm text-muted-foreground mb-2">{decision.rationale}</p>
                                         <div className="flex items-center gap-2 text-sm">
                                           <Star className="h-4 w-4 text-yellow-500" />
                                           <span>Initial confidence: {decision.day0_rating}/10</span>
                                         </div>
                                         {decision.rating_history.length > 1 && (
                                           <div className="flex items-center gap-2 text-sm mt-1">
                                             <Star className="h-4 w-4 text-blue-500" />
                                             <span>Current confidence: {decisionCurrentRating}/10</span>
                                           </div>
                                         )}
                                       </div>
                                       
                                       {/* Rating History */}
                                       {decision.rating_history.length > 1 && (
                                         <div className="p-4 bg-blue-50 rounded-lg">
                                           <h4 className="font-medium mb-2">Rating History</h4>
                                           <div className="space-y-1">
                                             {decision.rating_history.map((entry, index) => (
                                               <div key={index} className="flex justify-between text-sm">
                                                 <span>Day {entry.day}:</span>
                                                 <span>{entry.rating}/10</span>
                                               </div>
                                             ))}
                                           </div>
                                         </div>
                                       )}
                                       
                                        {/* Current Rating */}
                                        <ValidatedRatingSlider
                                          value={currentRating}
                                          onValueChange={setCurrentRating}
                                          label="Current Confidence Rating"
                                          showValidation={true}
                                        />
                                       
                                       {/* Outcome */}
                                       <div className="space-y-2">
                                         <Label htmlFor="outcome">How did this decision turn out?</Label>
                                         <Textarea
                                           id="outcome"
                                           placeholder="Describe the outcome and results of your decision..."
                                           value={outcome}
                                           onChange={(e) => setOutcome(e.target.value)}
                                           rows={3}
                                         />
                                       </div>
                                       
                                       {/* Lessons Learned */}
                                       <div className="space-y-2">
                                         <Label htmlFor="lessons">What did you learn?</Label>
                                         <Textarea
                                           id="lessons"
                                           placeholder="What would you do differently? What insights did you gain?"
                                           value={lessons}
                                           onChange={(e) => setLessons(e.target.value)}
                                           rows={3}
                                         />
                                       </div>
                                       
                                       {/* Additional Notes */}
                                       <div className="space-y-2">
                                         <Label htmlFor="notes">Additional Notes</Label>
                                         <Textarea
                                           id="notes"
                                           placeholder="Any other thoughts or observations..."
                                           value={reviewNotes}
                                           onChange={(e) => setReviewNotes(e.target.value)}
                                           rows={2}
                                         />
                                       </div>
                                     </div>
                                     
                                     <DialogFooter className="sticky bottom-0 bg-background pt-4">
                                      <Button variant="outline" onClick={() => setSelectedDecision(null)} disabled={isSubmittingReview}>
                                        Cancel
                                      </Button>
                                      <Button onClick={completeReview} disabled={isSubmittingReview}>
                                        {isSubmittingReview ? "Saving..." : "Complete Review"}
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="overdue">
            <div className="space-y-4">
              {overdueDecisions.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                    <p className="text-muted-foreground">No overdue reviews!</p>
                  </CardContent>
                </Card>
              ) : (
                overdueDecisions.map((decision) => {
                  const decisionCurrentRating = getCurrentRating(decision);
                  
                  return (
                    <Card
                      key={decision.id}
                      className="cursor-pointer border-red-200 hover:shadow-md transition-shadow"
                      {...clickableCardProps(() => onViewDecision(decision.id))}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg text-red-700">{decision.title}</CardTitle>
                            <CardDescription className="mt-1">
                              {decision.description}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm font-medium">{decisionCurrentRating}/10</span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Initial: {decision.day0_rating}/10
                              </div>
                            </div>
                            <Badge variant="destructive">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Overdue
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Overdue Reviews:</span>
                            {decision.overdueReviews.map((review) => (
                                <Badge key={review.day} variant="destructive">
                                  Day {review.day} - {review.due_date}
                                </Badge>
                              ))}
                          </div>
                          
                          <Button 
                            onClick={() => onViewDecision(decision.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Complete Overdue Review
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="upcoming">
            <div className="space-y-4">
              {upcomingDecisions.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Clock className="h-12 w-12 text-blue-500 mb-4" />
                    <p className="text-muted-foreground">No upcoming reviews in the next 7 days</p>
                  </CardContent>
                </Card>
              ) : (
                upcomingDecisions.map((decision) => {
                  const decisionCurrentRating = getCurrentRating(decision);
                  
                  return (
                    <Card
                      key={decision.id}
                      className="group cursor-pointer border-blue-200 hover:shadow-md transition-shadow"
                      {...clickableCardProps(() => onViewDecision(decision.id))}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg group-hover:text-primary">{decision.title}</CardTitle>
                            <CardDescription className="mt-1">
                              {decision.description}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm font-medium">{decisionCurrentRating}/10</span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Initial: {decision.day0_rating}/10
                              </div>
                            </div>
                            <Badge className="bg-blue-100 text-blue-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Upcoming
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Upcoming Reviews:</span>
                          {decision.upcomingReviews
                            .map((review) => (
                                <Badge key={review.day} className="bg-blue-100 text-blue-800">
                                  Day {review.day} - {review.due_date}
                                </Badge>
                              ))}
                          </div>
                          
                          <Button 
                            variant="outline"
                            onClick={() => onViewDecision(decision.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Decision
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
          </Tabs>
        </div>
    );
  }